import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ShieldCheck, Wrench, Award, Phone, Instagram, CheckCircle2, X } from 'lucide-react';
// Formats any input into the Ukrainian mask: +380 (XX) XXX-XX-XX
function formatUaPhone(value: string): string {
  let digits = value.replace(/\D/g, '');
  // Normalize prefix to 9 subscriber digits
  if (digits.startsWith('380')) digits = digits.slice(3);else if (digits.startsWith('0')) digits = digits.slice(1);
  digits = digits.slice(0, 9);
  if (digits.length === 0) return '';
  let out = '+380';
  if (digits.length > 0) out += ' (' + digits.slice(0, 2);
  if (digits.length >= 2) out += ')';
  if (digits.length > 2) out += ' ' + digits.slice(2, 5);
  if (digits.length > 5) out += '-' + digits.slice(5, 7);
  if (digits.length > 7) out += '-' + digits.slice(7, 9);
  return out;
}
// Ukrainian mobile prefixes supported by local operators and virtual networks
const UA_MOBILE_PREFIXES = new Set(['39', '50', '63', '66', '67', '68', '73', '75', '77', '89', '91', '92', '93', '94', '95', '96', '97', '98', '99']);
// Valid only for a complete Ukrainian mobile number in the displayed mask
function isValidUaPhone(value: string): boolean {
  const match = value.match(/^\+380 \((\d{2})\) \d{3}-\d{2}-\d{2}$/);
  return Boolean(match && UA_MOBILE_PREFIXES.has(match[1]));
}
function TikTokIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" data-id="element-204" data-mp="2u4erebq1bn0o">
      <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1-2.59-2.58 2.59 2.59 0 0 1 2.59-2.59c.27 0 .53.04.77.12V9.66a6.84 6.84 0 0 0-.77-.04 5.66 5.66 0 0 0-5.66 5.66 5.66 5.66 0 0 0 5.66 5.66 5.66 5.66 0 0 0 5.66-5.66V8.18a7.46 7.46 0 0 0 4.33 1.38V6.47a4.28 4.28 0 0 1-3.24-.65z" data-id="element-205" data-mp="384pk7kawnboh" />
    </svg>;
}
// Oversized logo used as a faded brand watermark behind the hero content
function WMark() {
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 hidden md:flex items-center justify-center overflow-hidden" data-id="element-206" data-mp="38vfod27u2m1z">
      {/* Soft olive glow behind the mark */}
      <div className="absolute w-[700px] h-[700px] rounded-full bg-[#6B7A4A]/[0.12] blur-3xl" data-id="element-207" data-mp="3itveb1v4jp9w" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.85
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative" data-id="element-208" data-mp="3mqzwd2m0xjum">
        <motion.img src="https://cdn.magicpatterns.com/uploads/3hvk9zQdwCEb1c9Sn2MUsk/white_logo_transparent_background.png" alt="" draggable={false} animate={{
        y: [0, -16, 0]
      }} transition={{
        repeat: Infinity,
        duration: 8,
        ease: 'easeInOut'
      }} className="w-[640px] lg:w-[900px] max-w-none object-contain select-none opacity-[0.16]" style={{
        filter: 'grayscale(1) brightness(2.1)'
      }} data-id="element-209" data-mp="5epa6023l2p7" />
      </motion.div>
    </div>;
}
// Decorative animated car silhouette that drives in along the bottom of the hero
function AnimatedCar() {
  const wheelSpin = {
    rotate: 360,
    transition: {
      repeat: Infinity,
      ease: 'linear' as const,
      duration: 1.1
    }
  };
  return <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-0 hidden lg:block overflow-hidden" data-id="element-210" data-mp="2ikdj96svio1i">
      {/* Road line */}
      <div className="absolute bottom-[46px] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#6B7A4A]/25 to-transparent" data-id="element-211" data-mp="28tizc933o0j8" />

      {/* Speed lines */}
      <div className="absolute bottom-[78px] left-0 flex flex-col gap-3" data-id="element-212" data-mp="36agbbmwsfu04">
        {[0, 1, 2].map(i => <motion.div key={i} className="h-px bg-[#6B7A4A]/40" initial={{
        width: 0,
        opacity: 0,
        x: 0
      }} animate={{
        width: [0, 60 - i * 12, 0],
        opacity: [0, 0.6, 0],
        x: [0, 120, 240]
      }} transition={{
        repeat: Infinity,
        duration: 1.6,
        delay: 1.2 + i * 0.18,
        ease: 'easeIn'
      }} style={{
        marginLeft: 24 + i * 18
      }} data-id="element-213" data-mp="31gvg22mkxe4m" />)}
      </div>

      {/* Car */}
      <motion.div className="absolute bottom-[36px] left-[6%]" initial={{
      x: -260,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} transition={{
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-214" data-mp="2v13jv9itfvre">
        <motion.div animate={{
        y: [0, -1.5, 0]
      }} transition={{
        repeat: Infinity,
        duration: 0.9,
        ease: 'easeInOut'
      }} data-id="element-215" data-mp="3sghc2mrn2w1r">
          <svg width="240" height="100" viewBox="0 0 240 100" fill="none" className="drop-shadow-[0_0_12px_rgba(107,122,74,0.25)]" data-id="element-216" data-mp="1na3b4lcf8e0">
            {/* Body silhouette */}
            <path d="M18 72 L18 62 C18 56 22 53 30 52 L60 48 C66 40 76 34 90 33 L132 33 C148 33 160 38 168 49 L196 53 C206 55 210 58 210 64 L210 72 Z" fill="#6B7A4A" fillOpacity="0.12" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-217" data-mp="aiputeymlbgf" />
            {/* Windows */}
            <path d="M74 47 L88 37 L120 37 L120 47 Z" fill="#6B7A4A" fillOpacity="0.2" data-id="element-218" data-mp="3nuqi1ot6g2gh" />
            <path d="M126 37 L150 37 C158 38 162 42 164 47 L126 47 Z" fill="#6B7A4A" fillOpacity="0.2" data-id="element-219" data-mp="182aqolwayi8z" />
            {/* Headlight */}
            <circle cx="206" cy="60" r="2" fill="#FBBC04" fillOpacity="0.9" data-id="element-220" data-mp="1b574o2fbig4l" />

            {/* Rear wheel */}
            <g data-id="element-221" data-mp="1f5inras188dy">
              <circle cx="58" cy="72" r="17" fill="#0a0a0a" stroke="#6B7A4A" strokeWidth="2" data-id="element-222" data-mp="1f96i2c5awytq" />
              <motion.g style={{
              transformBox: 'fill-box',
              transformOrigin: 'center'
            }} animate={wheelSpin} data-id="element-223" data-mp="1p35gb0297yge">
                <g transform="translate(58 72)" data-id="element-224" data-mp="11fxdcosat1hj">
                  <line x1="0" y1="-11" x2="0" y2="11" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-225" data-mp="11f5ijg6iz7sb" />
                  <line x1="-11" y1="0" x2="11" y2="0" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-226" data-mp="8p5h5rb8gcu7" />
                  <line x1="-8" y1="-8" x2="8" y2="8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-227" data-mp="177di3fqfvyw" />
                  <line x1="-8" y1="8" x2="8" y2="-8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-228" data-mp="3ne5m8zb3qxg9" />
                  <circle cx="0" cy="0" r="3" fill="#6B7A4A" data-id="element-229" data-mp="3ekk7hfyxtg6u" />
                </g>
              </motion.g>
            </g>

            {/* Front wheel */}
            <g data-id="element-230" data-mp="34p8scwjma6yk">
              <circle cx="178" cy="72" r="17" fill="#0a0a0a" stroke="#6B7A4A" strokeWidth="2" data-id="element-231" data-mp="34ps949xj6pb0" />
              <motion.g style={{
              transformBox: 'fill-box',
              transformOrigin: 'center'
            }} animate={wheelSpin} data-id="element-232" data-mp="2voaxb3r5p9s4">
                <g transform="translate(178 72)" data-id="element-233" data-mp="sdee36r0bqtp">
                  <line x1="0" y1="-11" x2="0" y2="11" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-234" data-mp="dhevh269u8ab" />
                  <line x1="-11" y1="0" x2="11" y2="0" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-235" data-mp="4ntinodo313j" />
                  <line x1="-8" y1="-8" x2="8" y2="8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-236" data-mp="3qtp22grd4873" />
                  <line x1="-8" y1="8" x2="8" y2="-8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-237" data-mp="3lwgrb18jn6fv" />
                  <circle cx="0" cy="0" r="3" fill="#6B7A4A" data-id="element-238" data-mp="2yad4g8tf8yt7" />
                </g>
              </motion.g>
            </g>
          </svg>
        </motion.div>
      </motion.div>
    </div>;
}
export function HeroSection() {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  return <section className="w-full min-h-screen flex items-center bg-zinc-950 text-white py-24 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-id="element-239" data-mp="3h8pctscie4nq">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" data-id="element-240" data-mp="3uyswcbk07k9h" />

      {/* Brand monogram watermark */}
      <WMark data-id="element-241" data-mp="8rkwforus5ku" />

      {/* Decorative driving car animation */}
      <AnimatedCar data-id="element-242" data-mp="8q1c9n32tads" />

      {/* Header logo - far left */}
      <div className="absolute top-8 left-6 md:left-12 lg:left-20 z-20" data-id="element-243" data-mp="3um0r1sjjqkj">
        <img src="https://cdn.magicpatterns.com/uploads/3hvk9zQdwCEb1c9Sn2MUsk/white_logo_transparent_background.png" alt="Win Code Garage logo" className="h-12 md:h-14 w-auto object-contain" data-id="element-244" data-mp="2qsv5xnwrqpzp" />
      </div>

      {/* Header contacts - far right */}
      <div className="absolute top-8 right-6 md:right-12 lg:right-20 z-20 flex items-center gap-4 md:gap-5" data-id="element-245" data-mp="2d1gj15zqq4nd">
        <a href="tel:+380774500450" className="group flex items-center gap-2 text-white hover:text-[#6B7A4A] transition-colors duration-200" aria-label="Зателефонувати: +380 77 450 0450" data-id="element-246" data-mp="39rz94g1mppjg">
          <Phone className="w-4 h-4 text-[#6B7A4A]" data-id="element-247" data-mp="30phsch4qtp3u" />
          <span className="hidden sm:inline font-['Oswald'] text-base md:text-lg font-bold tracking-wide whitespace-nowrap" data-id="element-248" data-mp="2vryaz8cr0w7o">
            +380 77 450 0450
          </span>
        </a>

        <div className="h-5 w-px bg-white/10 hidden sm:block" data-id="element-249" data-mp="1p99li3ia3rmd" />

        <div className="flex items-center gap-2.5" data-id="element-250" data-mp="1kaya3occ5921">
          <a href="https://instagram.com/win_code_garage" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 border border-white/10 text-white hover:border-[#6B7A4A] hover:text-[#6B7A4A] transition-colors duration-200" data-id="element-251" data-mp="1a8umc0ybvip0">
            <Instagram className="w-4 h-4" data-id="element-252" data-mp="1oxqurz4aqxq5" />
          </a>
          <a href="https://www.tiktok.com/@wincodegarage" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="p-2 border border-white/10 text-white hover:border-[#6B7A4A] hover:text-[#6B7A4A] transition-colors duration-200" data-id="element-253" data-mp="1ozbj3700pkfk">
            <TikTokIcon className="w-4 h-4" data-id="element-254" data-mp="27w317d3tpti1" />
          </a>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center" data-id="element-255" data-mp="8j8pn20t9jiu">
        {/* Left Column - Brand & Messaging */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="lg:col-span-7 flex flex-col justify-center" data-id="element-256" data-mp="dilmmflec6z5">
          <div className="flex items-center gap-3 mb-6" data-id="element-257" data-mp="ag7emneui1on">
            <div className="w-2 h-2 bg-[#6B7A4A] rounded-full pulse-dot" data-id="element-258" data-mp="aitl0jq02b5a" />
            <span className="font-['JetBrains_Mono'] text-xs text-[#6B7A4A] tracking-widest uppercase" data-id="element-259" data-mp="of70jnaz42h4">
              Хмельницький · Україна
            </span>
            <span className="text-zinc-700 mx-2" data-id="element-260" data-mp="y61pizfltg69">/</span>
            <span className="font-['JetBrains_Mono'] text-xs text-zinc-500 tracking-widest uppercase" data-id="element-261" data-mp="y5jjjb353nl5">
              UNIT-001
            </span>
          </div>

          <h1 className="font-['Oswald'] text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-widest leading-[0.9] mb-8" data-id="element-262" data-mp="176828j3hac4d">
            Win Code
            <br data-id="element-263" data-mp="133l2lw0kfmwm" />
            Garage
          </h1>

          <p className="text-zinc-400 text-lg md:text-xl max-w-xl leading-relaxed mb-10 font-['Inter']" data-id="element-264" data-mp="1lurl3utcs9cq">
            Автосервіс, заснований ветеранами. Військова дисципліна,
            безкомпромісні стандарти та повна прозорість на кожному етапі
            обслуговування вашого авто.
          </p>

          <div className="flex flex-wrap items-center gap-6" data-id="element-265" data-mp="1hr1u6yp7hfoj">
            <div className="flex items-center gap-2" data-id="element-266" data-mp="133gw5j76u6nh">
              <ShieldCheck className="w-4 h-4 text-[#6B7A4A]" data-id="element-267" data-mp="131njnn9w8s7k" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-268" data-mp="17u6zkqw1zzfj">
                Засновано ветеранами
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-269" data-mp="y3ugl33w0ebi">
              <Wrench className="w-4 h-4 text-[#6B7A4A]" data-id="element-270" data-mp="y4dors3qjocc" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-271" data-mp="k42wqlqow3pl">
                Сертифіковані майстри
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-272" data-mp="1rglumxyu9y8q">
              <Award className="w-4 h-4 text-[#6B7A4A]" data-id="element-273" data-mp="1rgbz6uv0yyvn" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-274" data-mp="1neqkd3yxkait">
                Гарантія на роботи
              </span>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Appointment Form */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: 'easeOut'
      }} className="lg:col-span-5 w-full max-w-md mx-auto lg:ml-auto lg:mr-0" data-id="element-275" data-mp="1dnmfix2yy61p">
          <div className="relative bg-neutral-900/50 border border-white/10 p-8" data-id="element-276" data-mp="usfi7ovoe1jw">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#6B7A4A]/60" data-id="element-277" data-mp="2u2dyyki502yv" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#6B7A4A]/60" data-id="element-278" data-mp="2p5x65czzt0fg" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#6B7A4A]/60" data-id="element-279" data-mp="2wnlmxm5qvq9a" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#6B7A4A]/60" data-id="element-280" data-mp="31ldfmie7rgej" />

            <div className="mb-8" data-id="element-281" data-mp="36g16gnwowmwa">
              <div className="font-['JetBrains_Mono'] text-[10px] text-zinc-500 tracking-widest mb-2 uppercase" data-id="element-282" data-mp="3bcrkz6sujafl">
                Форма запиту / BOOK-01
              </div>
              <h2 className="font-['Oswald'] text-3xl font-semibold text-white uppercase tracking-wide" data-id="element-283" data-mp="2drlte6x4cenl">
                Запис на ремонт
              </h2>
            </div>

            <form className="space-y-5" onSubmit={async e => {
            e.preventDefault();
            if (!isValidUaPhone(phone)) {
              setPhoneError('Введіть номер у форматі +380 (XX) XXX-XX-XX');
              return;
            }
            const formData = {
              name: e.target.name.value,
              phone: phone,
              car: e.target.car.value
            };
            await fetch('https://hook.eu1.make.com/g4xtnk7sfxu77g45aoekhui3xxbg5gtn', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(formData)
            });
            e.target.reset();
            setPhone('');
            setPhoneError('');
            setShowThankYou(true);
          }} data-id="element-284" data-mp="2nlvnbt1pll0f">
              <div className="space-y-2" data-id="element-285" data-mp="1u2udc5wke96s">
                <label htmlFor="name" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-286" data-mp="1pat30piyisje">
                  Ваше ім'я
                </label>
                <input type="text" id="name" name="name" placeholder="ІВАН ПЕТРЕНКО" className="w-full bg-neutral-950/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-700 focus:border-[#6B7A4A] focus:outline-none transition-colors rounded-none font-['Inter']" required data-id="element-287" data-mp="wku5e5qp5n34" />
              </div>

              <div className="space-y-2" data-id="element-288" data-mp="345j9vaqfq69z">
                <label htmlFor="phone" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-289" data-mp="3rwok9adlw6cw">
                  Номер телефону
                </label>
                <input type="tel" id="phone" name="phone" inputMode="tel" placeholder="+380 (XX) XXX-XX-XX" value={phone} onChange={e => {
                setPhone(formatUaPhone(e.target.value));
                if (phoneError) setPhoneError('');
              }} onFocus={e => {
                if (!e.target.value) setPhone('+380 (');
              }} maxLength={19} aria-invalid={!!phoneError} aria-describedby="phone-error" className={`w-full bg-neutral-950/50 border px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none transition-colors rounded-none font-['Inter'] ${phoneError ? 'border-red-500/70 focus:border-red-500' : 'border-white/10 focus:border-[#6B7A4A]'}`} required data-id="element-290" data-mp="3hyiq5ey1k4il" />
                {phoneError && <p id="phone-error" className="font-['JetBrains_Mono'] text-[10px] text-red-400 tracking-wider uppercase" data-id="element-291" data-mp="3c5elcygbc11d">
                    {phoneError}
                  </p>}
              </div>

              <div className="space-y-2" data-id="element-292" data-mp="3qmf4lv6b80z">
                <label htmlFor="car" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-293" data-mp="3pl1qaqoq9g3">
                  Модель авто
                </label>
                <input type="text" id="car" placeholder="МАРКА, МОДЕЛЬ, РІК" className="w-full bg-neutral-950/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-700 focus:border-[#6B7A4A] focus:outline-none transition-colors rounded-none font-['Inter']" required data-id="element-294" data-mp="l0gwuu95npm5" />
              </div>

              <button type="submit" className="group w-full bg-[#6B7A4A] hover:bg-[#7d8c5a] text-white font-['Oswald'] uppercase tracking-widest py-4 px-6 mt-4 flex items-center justify-between transition-colors duration-200" data-id="element-295" data-mp="13s9drqay9kdi">
                <span data-id="element-296" data-mp="1io8t9m0e2mso">Записатися на ремонт</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" data-id="element-297" data-mp="1ip0dk0ojcy3n" />
              </button>

              <div className="text-center mt-4" data-id="element-298" data-mp="2m67nh2n0084b">
                <span className="font-['JetBrains_Mono'] text-[10px] text-zinc-600 uppercase tracking-widest" data-id="element-299" data-mp="2m6z85if24lft">
                  Відповідь протягом 24 год · Без спаму
                </span>
              </div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Thank-you modal */}
      <AnimatePresence data-id="element-300" data-mp="3057cwcic4r1j">
        {showThankYou && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setShowThankYou(false)} role="dialog" aria-modal="true" aria-labelledby="thankyou-title" data-id="element-301" data-mp="354sbumxi8ze9">
            <motion.div initial={{
          opacity: 0,
          scale: 0.94,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.94,
          y: 16
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md bg-neutral-900 border border-white/10 p-10 text-center" data-id="element-302" data-mp="1c9rcku92jz0e">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#6B7A4A]/60" data-id="element-303" data-mp="1h70yw115vuk0" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#6B7A4A]/60" data-id="element-304" data-mp="2enzqpib5p3z3" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#6B7A4A]/60" data-id="element-305" data-mp="29tviywu2grwy" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#6B7A4A]/60" data-id="element-306" data-mp="80sc25re60mu" />

              <button type="button" onClick={() => setShowThankYou(false)} aria-label="Закрити" className="absolute top-4 right-4 p-1.5 text-zinc-500 hover:text-white transition-colors" data-id="element-307" data-mp="3ue0kpx3d1xzk">
                <X className="w-5 h-5" data-id="element-308" data-mp="3kei3hoeu4qb7" />
              </button>

              <div className="flex justify-center mb-6" data-id="element-309" data-mp="3pf63v0oyeji3">
                <div className="w-16 h-16 border border-[#6B7A4A]/40 bg-[#6B7A4A]/10 flex items-center justify-center" data-id="element-310" data-mp="3pcjowfzqh1pu">
                  <CheckCircle2 className="w-8 h-8 text-[#6B7A4A]" strokeWidth={1.5} data-id="element-311" data-mp="3gdyb0y57iave" />
                </div>
              </div>

              <div className="font-['JetBrains_Mono'] text-[10px] text-zinc-500 tracking-widest mb-3 uppercase" data-id="element-312" data-mp="1emuu5r4q402e">
                Запит прийнято / BOOK-OK
              </div>
              <h3 id="thankyou-title" className="font-['Oswald'] text-3xl font-bold text-white uppercase tracking-wide mb-4" data-id="element-313" data-mp="1oop40payn9q3">
                Дякуємо за запис!
              </h3>
              <p className="text-zinc-400 text-base leading-relaxed font-['Inter'] mb-8" data-id="element-314" data-mp="1sserfbfwfl9j">
                Ваша заявка успішно відправлена. Наш майстер зв'яжеться з вами
                протягом 24 годин для підтвердження запису.
              </p>

              <button type="button" onClick={() => setShowThankYou(false)} className="w-full bg-[#6B7A4A] hover:bg-[#7d8c5a] text-white font-['Oswald'] uppercase tracking-widest py-3.5 px-6 transition-colors duration-200" data-id="element-315" data-mp="22herky650mpb">
                Зрозуміло
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}